/**
 * Extract a human-readable message from a rejected API call.
 *
 * The request middleware (services/middleware/request.ts) rejects with a few
 * different shapes depending on status:
 *   { status, body, resBody }   (e.g. 401)
 *   { status, body }            (handled statuses)
 *   the raw response body       (error_description path)
 * The backend error body itself is `{ detail, code }` (stable codes) or legacy
 * `{ detail }`. This helper looks through those shapes for the best message and
 * falls back to a generic string so a toast always has something to show.
 */
export function getApiErrorMessage(
  err: any,
  fallback = "Something went wrong. Please try again."
): string {
  if (!err) return fallback;

  if (typeof err === "string") return err;

  // The backend body may sit at .resBody, .body, or be the error itself.
  const candidates = [err.resBody, err.body, err];
  for (const c of candidates) {
    if (!c) continue;
    if (typeof c === "string") return c;
    if (typeof c.detail === "string") return c.detail;
    if (typeof c.message === "string") return c.message;
    if (typeof c.error_description === "string") return c.error_description;
  }

  if (typeof err.message === "string") return err.message;

  return fallback;
}

/** Stable backend auth error code, when present (see app/common/auth_errors.py). */
export function getApiErrorCode(err: any): string | undefined {
  return err?.resBody?.code ?? err?.body?.code ?? err?.code ?? undefined;
}
